import React, { useContext } from "react";
import IOTSvg from "../../../../../../images/printers/iot.svg";
import GCPrintSvg from "../../../../../../images/printers/gc_print.svg";
import PrintServerSVG from "../../../../../../images/printers/print_server.svg";

import { PrintersRegistryInfo } from "@grabcad/company-server-shared/dist/types";
import { DataSources, SourceChip, Tooltip } from "./PrinterDataSources.styles";
import { ApplicationContext } from "../../../../../../components/ApplicationProvider";

type PrinterDataSourcesProps = Pick<PrintersRegistryInfo, "dataSources" | "lastSeenOn">;

type DataSourceChip = {
    icon?: string;
    label: string;
};

type IoTSource = "Gateway" | "CloudDirect";

const formatLastSeen = (timestamp?: string): string => {
    if (!timestamp) {
        return "Never";
    }

    const lastSeen = new Date(timestamp);
    const diffInMinutes = Math.floor((Date.now() - lastSeen.getTime()) / 60000);

    if (diffInMinutes < 1) {
        return "Last seen: Just now";
    }

    if (diffInMinutes < 60) {
        return `Last seen: ${diffInMinutes}m ago`;
    }

    const diffInHours = Math.floor(diffInMinutes / 60);

    if (diffInHours < 24) {
        return `Last seen: ${diffInHours}h ago`;
    }

    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInDays < 7) {
        return `Last seen: ${diffInDays}d ago`;
    }

    return `Last seen: ${lastSeen.toLocaleDateString(undefined, {
        day: "2-digit",
        month: "short",
        year: "numeric",
    })}`;
};

const PrinterDataSources: React.FC<PrinterDataSourcesProps> = ({ dataSources, lastSeenOn }) => {
    const { t } = useContext(ApplicationContext);

    if (!dataSources?.length) {
        return null;
    }

    const DATA_SOURCE_CHIPS: Record<string, DataSourceChip> = {
        IoT: {
            icon: IOTSvg,
            label: lastSeenOn?.Gateway
                ? t("devices.printer_card.printer_data_sources.gateway")
                : t("devices.printer_card.printer_data_sources.cloud_direct"),
        },
        "Print Server": {
            icon: PrintServerSVG,
            label: t("devices.printer_card.printer_data_sources.print_server"),
        },
        "GrabCAD Print": {
            icon: GCPrintSvg,
            label: t("devices.printer_card.printer_data_sources.print"),
        },
    };

    const getLastSeenEntries = (source: string): Array<[string, string]> => {
        if (!lastSeenOn) {
            return [];
        }

        if (source === "IoT") {
            return (["Gateway", "CloudDirect"] as IoTSource[])
                .filter((iotSource) => Boolean(lastSeenOn[iotSource]))
                .map((iotSource) => [iotSource, lastSeenOn[iotSource]!] as [string, string]);
        }

        const timestamp = lastSeenOn[source];

        return timestamp ? [[source, timestamp]] : [];
    };

    return (
        <DataSources data-testid="printer-data-sources">
            {dataSources.map((source) => {
                const chip = DATA_SOURCE_CHIPS[source] ?? {
                    label: source,
                };

                const lastSeenEntries = getLastSeenEntries(source);

                return (
                    <SourceChip key={source} data-testid={`source-chip-${source}`}>
                        {chip.icon && (
                            <img
                                src={chip.icon}
                                alt=""
                                aria-hidden="true"
                                className="source-chip-image"
                            />
                        )}

                        <span>{chip.label}</span>

                        {lastSeenEntries.length > 0 && (
                            <Tooltip data-testid="last-seen-tooltip">
                                {lastSeenEntries.map(([sourceName, timestamp]) => (
                                    <div key={sourceName}>
                                        <strong>{formatLastSeen(timestamp)}</strong>
                                    </div>
                                ))}
                            </Tooltip>
                        )}
                    </SourceChip>
                );
            })}
        </DataSources>
    );
};

export default React.memo(PrinterDataSources);
